import { ReactNode } from 'react';
import { Badge, Box, Button, Heading, HStack, Text, VStack } from '@chakra-ui/react';
import { DashboardTemplate } from '../../components/templates/DashboardTemplate.tsx';

function yearOf(value: unknown): number | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const match = value.match(/^(\d{4})/);
  if (!match) return null;
  const year = Number(match[1]);
  if (year < 1900 || year > 3000) return null;
  return year;
}

export function agreementYears(startDate: unknown, createdAt: unknown): number[] {
  const current = new Date().getFullYear();
  const start = Math.min(yearOf(startDate) ?? yearOf(createdAt) ?? current, current);
  return Array.from({ length: current - start + 1 }, (_, index) => current - index);
}

export function financialErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    if (typeof record.detail === 'string') return record.detail;
    if (typeof record.error === 'string') return record.error;
  }
  return 'Não foi possível carregar os dados financeiros.';
}

export const fieldProps = {
  bg: 'gray.50',
  borderColor: 'gray.200',
  borderRadius: 'lg',
  size: 'sm' as const,
  _hover: { borderColor: 'gray.300' },
  focusBorderColor: 'gray.400',
};

export const tableSx = {
  th: {
    textTransform: 'uppercase',
    fontSize: '11px',
    letterSpacing: '0.06em',
    color: 'var(--chakra-colors-gray-500)',
    fontWeight: 700,
    bg: 'var(--chakra-colors-gray-50)',
    borderColor: 'var(--chakra-colors-gray-100)',
    py: '12px',
  },
  td: {
    borderColor: 'var(--chakra-colors-gray-100)',
    py: '14px',
    fontSize: '14px',
    verticalAlign: 'middle',
  },
  'tbody tr:last-of-type td': { borderBottomWidth: 0 },
};

export function FinancialShell({
  title,
  description,
  maxW = '1120px',
  children,
}: {
  title: string;
  description: string;
  maxW?: string;
  children: ReactNode;
}) {
  return (
    <DashboardTemplate>
      <VStack align="stretch" spacing={6} w="100%" p={{ base: 4, md: 8 }} maxW={maxW}>
        <Box>
          <Heading as="h1" size="lg" letterSpacing="-0.02em">
            {title}
          </Heading>
          <Text mt={1} color="gray.500" fontSize="sm">
            {description}
          </Text>
        </Box>
        {children}
      </VStack>
    </DashboardTemplate>
  );
}

export function FinancialCard({ children, p = 0 }: { children: ReactNode; p?: number | string }) {
  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="2xl" boxShadow="sm" overflow="hidden" p={p}>
      {children}
    </Box>
  );
}

export function FilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box minW={{ base: '100%', md: '148px' }} flex="1">
      <Text fontSize="xs" mb={1.5} color="gray.500" fontWeight="600">
        {label}
      </Text>
      {children}
    </Box>
  );
}

export function FinancialTabs<T extends string>({
  value,
  onChange,
  options,
  color,
}: {
  value: T;
  onChange: (value: T) => void;
  options: { id: T; label: string }[];
  color: string;
}) {
  return (
    <HStack spacing={1} bg="white" borderWidth="1px" borderColor="gray.100" boxShadow="sm" p="4px" borderRadius="full" w="fit-content" flexWrap="wrap">
      {options.map((item) => {
        const selected = value === item.id;
        return (
          <Box
            key={item.id}
            as="button"
            type="button"
            onClick={() => onChange(item.id)}
            px={4}
            py={2}
            borderRadius="full"
            fontSize="sm"
            fontWeight="600"
            bg={selected ? color : 'transparent'}
            color={selected ? 'white' : 'gray.600'}
          >
            {item.label}
          </Box>
        );
      })}
    </HStack>
  );
}

export function FinancialNotice({ children, tone = 'muted' }: { children: ReactNode; tone?: 'muted' | 'error' }) {
  return (
    <FinancialCard p={8}>
      <Text textAlign="center" color={tone === 'error' ? 'red.600' : 'gray.500'} fontSize="sm">
        {children}
      </Text>
    </FinancialCard>
  );
}

export function FinancialPagination({
  page,
  count,
  hasPrevious,
  hasNext,
  isLoading,
  onPrevious,
  onNext,
  itemLabel,
  flush = false,
}: {
  page: number;
  count?: number;
  hasPrevious?: boolean;
  hasNext?: boolean;
  isLoading: boolean;
  onPrevious: () => void;
  onNext: () => void;
  itemLabel: [singular: string, plural: string];
  flush?: boolean;
}) {
  return (
    <HStack spacing={2} justifyContent="flex-end" flex="1" pt={flush ? 0 : 2}>
      <Button onClick={onPrevious} isDisabled={!hasPrevious} variant="ghost" size="sm" color="gray.600">
        Anterior
      </Button>
      <Text fontSize="sm" color="gray.600" fontWeight="600" px={2}>
        {page}
      </Text>
      {typeof count === 'number' && (
        <Badge colorScheme="gray" borderRadius="full" px={2} py={0.5} fontSize="2xs" textTransform="none">
          {count} {count === 1 ? itemLabel[0] : itemLabel[1]}
        </Badge>
      )}
      <Button onClick={onNext} isDisabled={!hasNext} variant="ghost" size="sm" color="gray.600" isLoading={isLoading}>
        Próximo
      </Button>
    </HStack>
  );
}
